/**
 * GMA-1627 (CompassCare Web 12/14 — Messages: threads, broadcasts,
 * alert-linked comms) — page composition.
 *
 * Three-rail Messages screen (docs/design-mockups/CompassCare Messages.dc.html):
 * the threads rail (kind filter chips + permission-gated thread list), the
 * conversation pane (system events, delivery receipts, alert-linked header
 * with Escalate-to-call-tree, template composer), and the right rail (either
 * the broadcast composer or the linked-context card + Recent Broadcasts).
 *
 * The privacy rules come from the pure messagesModel and are enforced
 * VISIBLY, never re-implemented:
 *   - the rail runs every thread through canSeeThread(viewer, thread) — a
 *     family viewer only ever sees their own client's familyVisible threads,
 *     and a malformed thread fails closed for every role;
 *   - sends into a family thread are re-checked against canSeeThread AS the
 *     family recipient — a revoked thread blocks the composer with a
 *     role="alert" error (same pattern as ReportsPage's family-blocked report
 *     types);
 *   - broadcast cohorts resolve through resolveBroadcastRecipients for
 *     caregiver cohorts, and family cohorts are permission-gated per client
 *     through canSeeThread (fail closed) — blocked recipients are surfaced,
 *     and a cohort with zero permitted recipients cannot be sent at all.
 *
 * Presentational + fail-safe: all data is injected by the route (testable)
 * and defaults to empty states; null / malformed rows can never break the
 * render. Colors/fonts/severity come from the single design-token set. a11y:
 * every control is a native <button> (aria-pressed for filters/cohorts/
 * selection), thread switches and sends are announced through a polite
 * role="status" live region, and unread/receipt/blocked state is carried by
 * text — never color alone.
 */
import React, { useState } from "react";
import PropTypes from "prop-types";
import SeverityBadge from "../../components/web/SeverityBadge";
import {
  MESSAGE_TEMPLATES,
  canSeeThread,
  resolveBroadcastRecipients,
} from "./messagesModel";
import { formatRelativeTime } from "../../utils/webFormat";
import { theme } from "../../styles/designTokens";

// Draft bodies for the model's template catalog (mockup copy). The catalog —
// which templates exist and their labels — stays single-sourced from
// MESSAGE_TEMPLATES; this map only supplies the prefill prose.
const TEMPLATE_BODIES = Object.freeze({
  wellness_check:
    "Could you do an in-person wellness check and confirm the client is okay?",
  safety_message:
    "Safety note: please review the current care-plan precautions for this client today.",
  hydration_reminder:
    "Heat advisory in effect — please offer fluids and confirm hydration.",
  shift_handoff:
    "Shift handoff: no open concerns beyond what’s noted. Active watches carried forward.",
});

// Threads-rail kind filters. The mockup folds alert-linked threads into the
// Clients chip (an alert thread IS a client conversation).
const THREAD_FILTERS = Object.freeze([
  Object.freeze({ id: "all", label: "All" }),
  Object.freeze({ id: "client", label: "Clients" }),
  Object.freeze({ id: "home", label: "Homes" }),
  Object.freeze({ id: "family", label: "Family" }),
]);

const DELIVERY_LABELS = Object.freeze({
  read: "Read",
  delivered: "Delivered",
  sent: "Sent",
});

const isRecord = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const asRows = (v) => (Array.isArray(v) ? v.filter(isRecord) : []);
const asText = (v, fallback = "") =>
  typeof v === "string" && v.trim() !== "" ? v : fallback;

const panelStyle = {
  background: theme.surface,
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
};

const panelHeadingStyle = {
  margin: 0,
  padding: "12px 14px 8px",
  fontSize: 12,
  color: theme.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const chipStyle = (pressed) => ({
  minHeight: 28,
  padding: "4px 10px",
  borderRadius: 999,
  border: `1px solid ${pressed ? theme.textMuted : theme.border}`,
  background: pressed ? theme.bg : "transparent",
  color: pressed ? theme.textPrimary : theme.textMuted,
  cursor: "pointer",
  fontFamily: theme.fontUI,
  fontSize: 12,
  fontWeight: 600,
});

const primaryButtonStyle = (enabled) => ({
  minHeight: 36,
  padding: "8px 16px",
  border: "none",
  borderRadius: 6,
  background: enabled ? "#8B7CF6" : theme.border,
  color: enabled ? "#FFFFFF" : theme.textMuted,
  cursor: enabled ? "pointer" : "not-allowed",
  fontFamily: theme.fontUI,
  fontSize: 13,
  fontWeight: 700,
});

const textareaStyle = {
  width: "100%",
  minHeight: 56,
  resize: "vertical",
  boxSizing: "border-box",
  background: theme.bg,
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  padding: "9px 11px",
  color: theme.textPrimary,
  fontFamily: theme.fontUI,
  fontSize: 13,
  lineHeight: 1.45,
  outline: "none",
};

const alertTextStyle = {
  margin: 0,
  fontSize: 12,
  color: "#F0524E",
};

/** Whether the thread's family recipients may see it — the REAL model gate,
 * evaluated AS the family participant (fail closed on anything malformed). */
const familyCanSee = (thread) =>
  canSeeThread(
    { role: "family", clientIds: [thread && thread.clientId] },
    thread,
  );

/**
 * Resolve a broadcast cohort's permitted recipients through the model.
 *   - caregiver cohorts: resolveBroadcastRecipients (dedupe + overlap rules).
 *   - family cohorts: each contact must own a cohort client AND pass
 *     canSeeThread as a family viewer against that client's visibility flag
 *     (fail closed — an unknown client blocks, never exposes).
 * Returns { recipients, blocked } so the UI can surface the blocked count.
 */
function resolveCohortRecipients(cohort, caregivers, familyContacts, familyVisibility) {
  if (!isRecord(cohort)) return { recipients: [], blocked: 0 };
  if (cohort.kind === "family") {
    const cohortIds = Array.isArray(cohort.clientIds) ? cohort.clientIds : [];
    const visibility = isRecord(familyVisibility) ? familyVisibility : {};
    const recipients = [];
    let blocked = 0;
    for (const contact of asRows(familyContacts)) {
      const owned = Array.isArray(contact.clientIds) ? contact.clientIds : [];
      const shared = owned.filter((id) => cohortIds.includes(id));
      if (shared.length === 0) continue; // not in this cohort at all
      const permitted = shared.some((clientId) =>
        canSeeThread(
          { role: "family", clientIds: owned },
          { clientId, familyVisible: visibility[clientId] === true },
        ),
      );
      if (permitted) recipients.push(contact);
      else blocked += 1;
    }
    return { recipients, blocked };
  }
  return {
    recipients: resolveBroadcastRecipients(asRows(caregivers), cohort),
    blocked: 0,
  };
}

export default function MessagesPage({
  threads = [],
  caregivers = [],
  familyContacts = [],
  familyVisibility = {},
  cohorts = [],
  recentBroadcasts = [],
  viewer = { role: "care_team" },
  now,
  onSendMessage,
  onSendBroadcast,
  onEscalate,
  onOpenAlert,
}) {
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState("");
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [cohortId, setCohortId] = useState(null);
  const [broadcastDraft, setBroadcastDraft] = useState("");
  const [sentByThread, setSentByThread] = useState({});
  const [announcement, setAnnouncement] = useState("");

  // Permission gate FIRST (real model, fail closed), then the kind filter.
  const visibleThreads = asRows(threads).filter((t) => canSeeThread(viewer, t));
  const filteredThreads =
    filter === "all"
      ? visibleThreads
      : visibleThreads.filter(
          (t) => t.kind === filter || (filter === "client" && t.kind === "alert"),
        );

  const selected =
    visibleThreads.find((t) => t.id === selectedId) || filteredThreads[0] || null;

  const cohortRows = asRows(cohorts);
  const activeCohort =
    cohortRows.find((c) => c.id === cohortId) || cohortRows[0] || null;
  const { recipients, blocked } = resolveCohortRecipients(
    activeCohort,
    caregivers,
    familyContacts,
    familyVisibility,
  );

  // Family-gating on the composer: a family thread whose recipients fail the
  // model's visibility check must not accept a send (blocked visibly).
  const sendBlocked = selected && selected.kind === "family" && !familyCanSee(selected);
  const canSend = Boolean(selected) && !sendBlocked && draft.trim() !== "";

  const baseMessages = selected ? asRows(selected.messages) : [];
  const localMessages = (selected && sentByThread[selected.id]) || [];
  const conversationMessages = baseMessages.concat(localMessages);

  const selectThread = (thread) => {
    setSelectedId(thread.id);
    setBroadcastOpen(false);
    setAnnouncement(`Viewing ${asText(thread.name, "conversation")}`);
  };

  const sendMessage = () => {
    if (!canSend || !selected) return;
    const text = draft.trim();
    if (typeof onSendMessage === "function") {
      onSendMessage({ threadId: selected.id, text });
    }
    setSentByThread((prev) => ({
      ...prev,
      [selected.id]: (prev[selected.id] || []).concat({
        kind: "message",
        self: true,
        sender: "You",
        role: "Dispatcher",
        text,
        at: typeof now === "number" ? now : Date.now(),
        receipt: "sent",
      }),
    }));
    setDraft("");
    setAnnouncement(
      `Message sent to ${asText(selected.name, "conversation")} · logged to audit trail`,
    );
  };

  const sendBroadcast = () => {
    if (!activeCohort || recipients.length === 0 || broadcastDraft.trim() === "") return;
    const text = broadcastDraft.trim();
    if (typeof onSendBroadcast === "function") {
      onSendBroadcast({
        cohortId: activeCohort.id,
        recipientIds: recipients.map((r) => r.id),
        text,
      });
    }
    setBroadcastDraft("");
    setBroadcastOpen(false);
    setAnnouncement(
      `Broadcast sent to ${recipients.length} recipients (${asText(
        activeCohort.label,
        "cohort",
      )}) — recipient list logged`,
    );
  };

  const context = selected && isRecord(selected.context) ? selected.context : null;

  return (
    <div
      data-testid="messages-page"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(260px, 340px) minmax(0, 1fr) minmax(240px, 312px)",
        alignItems: "start",
        gap: 16,
        padding: 16,
        background: theme.bg,
        color: theme.textPrimary,
        fontFamily: theme.fontUI,
      }}
    >
      {/* Polite live region: announces thread switches, sends, and broadcast
          confirmations to assistive tech without stealing focus. */}
      <p
        data-testid="messages-status"
        role="status"
        aria-live="polite"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {announcement}
      </p>

      {/* ============ LEFT: threads rail ============ */}
      <section aria-label="Conversations" style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {THREAD_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              data-testid={`thread-filter-${f.id}`}
              aria-pressed={filter === f.id}
              onClick={() => setFilter(f.id)}
              style={chipStyle(filter === f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          data-testid="new-broadcast"
          onClick={() => setBroadcastOpen(true)}
          style={primaryButtonStyle(true)}
        >
          New Broadcast
        </button>

        {filteredThreads.length === 0 ? (
          <p
            data-testid="threads-empty"
            style={{ ...panelStyle, padding: 14, margin: 0, color: theme.textMuted, fontSize: 13 }}
          >
            No conversations
          </p>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
            {filteredThreads.map((t, index) => {
              const isSelected = selected !== null && selected.id === t.id;
              return (
                <li key={asText(t.id, `thread-${index}`)}>
                  <button
                    type="button"
                    data-testid="thread-row"
                    aria-pressed={isSelected}
                    onClick={() => selectThread(t)}
                    style={{
                      display: "grid",
                      gap: 4,
                      width: "100%",
                      textAlign: "left",
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: `1px solid ${isSelected ? theme.textMuted : theme.border}`,
                      background: isSelected ? theme.bg : theme.surface,
                      color: theme.textPrimary,
                      cursor: "pointer",
                      fontFamily: theme.fontUI,
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600 }}>
                        {asText(t.name, "Conversation")}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontFamily: theme.fontMono,
                          color: theme.textMuted,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatRelativeTime(t.lastMessageAt, now)}
                      </span>
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 11, color: theme.textMuted }}>
                        {asText(t.tag)}
                      </span>
                      <SeverityBadge level={t.severity} style={{ fontSize: 10 }} />
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: 12,
                          color: theme.textMuted,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {asText(t.preview)}
                      </span>
                      {/* unread state carried by TEXT, never a colored dot alone */}
                      {typeof t.unread === "number" && t.unread > 0 && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 999,
                            border: `1px solid ${theme.border}`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {`${t.unread} unread`}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ============ CENTER: conversation ============ */}
      <section data-testid="conversation" aria-label="Conversation" style={panelStyle}>
        {selected === null ? (
          <p
            data-testid="conversation-empty"
            style={{ padding: 16, margin: 0, color: theme.textMuted }}
          >
            No conversation selected — pick a thread from the rail.
          </p>
        ) : (
          <div style={{ display: "grid" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 10,
                padding: "12px 16px",
                borderBottom: `1px solid ${theme.border}`,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ margin: 0, fontSize: 15 }}>
                  {asText(selected.name, "Conversation")}
                </h2>
                <p style={{ margin: 0, fontSize: 11, color: theme.textMuted }}>
                  {[asText(selected.sub), asText(selected.channel)]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              {selected.kind === "alert" && (
                <>
                  <span
                    data-testid="conversation-alert-chip"
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "3px 10px",
                      borderRadius: 999,
                      border: "1px solid #F0524E",
                      color: "#F0524E",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {asText(selected.alertLabel, "Alert-linked")}
                  </span>
                  <button
                    type="button"
                    data-testid="conversation-escalate"
                    onClick={() => {
                      if (typeof onEscalate === "function") onEscalate(selected.id);
                      setAnnouncement(
                        `Escalated ${asText(selected.name, "conversation")} to the call tree`,
                      );
                    }}
                    style={{ ...chipStyle(false), color: theme.textPrimary }}
                  >
                    Escalate to call tree
                  </button>
                </>
              )}
            </div>

            {asRows(selected.participants).length > 0 && (
              <p
                style={{
                  margin: 0,
                  padding: "8px 16px",
                  borderBottom: `1px solid ${theme.border}`,
                  fontSize: 11,
                  color: theme.textMuted,
                }}
              >
                {"Participants: "}
                {asRows(selected.participants)
                  .map((p) =>
                    [asText(p.name, "Participant"), asText(p.role)]
                      .filter(Boolean)
                      .join(" — "),
                  )
                  .join(" · ")}
              </p>
            )}

            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: 16,
                display: "grid",
                gap: 10,
              }}
            >
              {conversationMessages.map((m, index) => {
                if (m.kind === "system") {
                  return (
                    <li
                      key={`sys-${index}`}
                      data-testid="system-row"
                      style={{
                        justifySelf: "center",
                        maxWidth: "90%",
                        padding: "6px 12px",
                        borderRadius: 999,
                        border: `1px solid ${theme.border}`,
                        fontSize: 11,
                        color: theme.textMuted,
                        textAlign: "center",
                      }}
                    >
                      {asText(m.text, "System event")}
                      {formatRelativeTime(m.at, now)
                        ? ` · ${formatRelativeTime(m.at, now)}`
                        : ""}
                    </li>
                  );
                }
                const mine = m.self === true;
                return (
                  <li
                    key={`msg-${index}`}
                    data-testid="message-row"
                    style={{
                      justifySelf: mine ? "end" : "start",
                      maxWidth: "82%",
                      padding: "8px 12px",
                      borderRadius: 8,
                      border: `1px solid ${theme.border}`,
                      background: mine ? "rgba(139,124,246,.12)" : theme.bg,
                    }}
                  >
                    <p style={{ margin: 0, fontSize: 11 }}>
                      <span style={{ fontWeight: 700 }}>
                        {asText(m.sender, "Participant")}
                      </span>
                      {asText(m.role) && (
                        <span style={{ color: theme.textMuted }}>
                          {" · "}
                          <span>{m.role}</span>
                        </span>
                      )}
                    </p>
                    <p style={{ margin: "4px 0 0", fontSize: 13, lineHeight: 1.5 }}>
                      {asText(m.text, "—")}
                    </p>
                    <p
                      style={{
                        margin: "6px 0 0",
                        fontSize: 10,
                        fontFamily: theme.fontMono,
                        color: theme.textMuted,
                      }}
                    >
                      {formatRelativeTime(m.at, now)}
                      {/* delivery state carried by text, not icon color alone */}
                      {DELIVERY_LABELS[m.receipt] && (
                        <>
                          {formatRelativeTime(m.at, now) ? " · " : ""}
                          <span>{DELIVERY_LABELS[m.receipt]}</span>
                        </>
                      )}
                    </p>
                  </li>
                );
              })}
            </ul>

            <div
              style={{
                display: "grid",
                gap: 8,
                padding: 16,
                borderTop: `1px solid ${theme.border}`,
              }}
            >
              <div
                role="group"
                aria-label="Message templates"
                style={{ display: "flex", gap: 6, flexWrap: "wrap" }}
              >
                {Object.keys(MESSAGE_TEMPLATES).map((key) => (
                  <button
                    key={key}
                    type="button"
                    data-testid="template-chip"
                    onClick={() => setDraft(TEMPLATE_BODIES[key] || MESSAGE_TEMPLATES[key].label)}
                    style={chipStyle(false)}
                  >
                    {MESSAGE_TEMPLATES[key].label}
                  </button>
                ))}
              </div>
              {sendBlocked && (
                <p data-testid="composer-blocked" role="alert" style={alertTextStyle}>
                  Blocked: family recipients do not have permission for this
                  thread — sends are disabled until per-client family visibility
                  is restored.
                </p>
              )}
              <textarea
                data-testid="composer-input"
                aria-label="Message draft"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Write a message…"
                style={textareaStyle}
              />
              <button
                type="button"
                data-testid="composer-send"
                disabled={!canSend}
                onClick={sendMessage}
                style={primaryButtonStyle(canSend)}
              >
                Send
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ============ RIGHT: broadcast composer OR context + recent ============ */}
      <div style={{ display: "grid", gap: 16 }}>
        {broadcastOpen ? (
          <section
            data-testid="broadcast-composer"
            aria-label="New broadcast"
            style={panelStyle}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <h2 style={panelHeadingStyle}>New Broadcast</h2>
              <button
                type="button"
                data-testid="broadcast-close"
                onClick={() => setBroadcastOpen(false)}
                style={{ ...chipStyle(false), margin: "8px 14px 0 0" }}
              >
                Close
              </button>
            </div>
            <div style={{ display: "grid", gap: 10, padding: "0 14px 14px" }}>
              <div
                role="group"
                aria-label="Recipient cohort"
                style={{ display: "grid", gap: 6 }}
              >
                {cohortRows.map((c, index) => {
                  const pressed = activeCohort !== null && activeCohort.id === c.id;
                  return (
                    <button
                      key={asText(c.id, `cohort-${index}`)}
                      type="button"
                      data-testid={`broadcast-cohort-${asText(c.id, index)}`}
                      aria-pressed={pressed}
                      onClick={() => setCohortId(c.id)}
                      style={{ ...chipStyle(pressed), textAlign: "left" }}
                    >
                      {asText(c.label, "Cohort")}
                    </button>
                  );
                })}
              </div>
              {activeCohort && (
                <p
                  data-testid="broadcast-target"
                  style={{ margin: 0, fontSize: 12, color: theme.textPrimary }}
                >
                  {`Sending to: ${asText(activeCohort.label, "Cohort")} · ${
                    recipients.length
                  } recipient${recipients.length === 1 ? "" : "s"}`}
                </p>
              )}
              {blocked > 0 && recipients.length > 0 && (
                <p
                  data-testid="broadcast-blocked-note"
                  style={{ margin: 0, fontSize: 11, color: theme.textMuted }}
                >
                  {`${blocked} blocked by per-client family permissions (not included)`}
                </p>
              )}
              {activeCohort && recipients.length === 0 && (
                <p data-testid="broadcast-blocked" role="alert" style={alertTextStyle}>
                  Blocked: no permitted recipients in this cohort — family
                  visibility is off for every matching client.
                </p>
              )}
              <textarea
                data-testid="broadcast-input"
                aria-label="Broadcast draft"
                value={broadcastDraft}
                onChange={(e) => setBroadcastDraft(e.target.value)}
                placeholder="Broadcast message…"
                style={textareaStyle}
              />
              <button
                type="button"
                data-testid="broadcast-send"
                disabled={
                  !activeCohort || recipients.length === 0 || broadcastDraft.trim() === ""
                }
                onClick={sendBroadcast}
                style={primaryButtonStyle(
                  Boolean(activeCohort) &&
                    recipients.length > 0 &&
                    broadcastDraft.trim() !== "",
                )}
              >
                Send Broadcast
              </button>
              <p style={{ margin: 0, fontSize: 10, color: theme.textMuted }}>
                Recipient list is recorded to the audit trail. Family visibility
                respects per-client permissions.
              </p>
            </div>
          </section>
        ) : (
          <>
            {context && (
              <section data-testid="context-card" aria-label="Linked context" style={panelStyle}>
                <h2 style={panelHeadingStyle}>{asText(context.title, "Context")}</h2>
                <dl style={{ margin: 0, padding: "0 14px 10px", display: "grid", gap: 6 }}>
                  {asRows(context.rows).map((row, index) => (
                    <div
                      key={`${asText(row.k, "row")}-${index}`}
                      style={{ display: "flex", gap: 8, fontSize: 12 }}
                    >
                      <dt style={{ color: theme.textMuted, minWidth: 88 }}>
                        {asText(row.k, "—")}
                      </dt>
                      <dd style={{ margin: 0 }}>{asText(row.v, "—")}</dd>
                    </div>
                  ))}
                </dl>
                {selected && selected.kind === "alert" && (
                  <div style={{ padding: "0 14px 14px" }}>
                    <button
                      type="button"
                      data-testid="context-open-alert"
                      onClick={() => {
                        if (typeof onOpenAlert === "function") onOpenAlert(selected.id);
                      }}
                      style={chipStyle(false)}
                    >
                      Open alert →
                    </button>
                  </div>
                )}
              </section>
            )}

            <section
              data-testid="recent-broadcasts"
              aria-label="Recent broadcasts"
              style={panelStyle}
            >
              <h2 style={panelHeadingStyle}>Recent Broadcasts</h2>
              {asRows(recentBroadcasts).length === 0 ? (
                <p
                  data-testid="recent-broadcasts-empty"
                  style={{ padding: "0 14px 14px", margin: 0, color: theme.textMuted, fontSize: 13 }}
                >
                  No broadcasts yet
                </p>
              ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: "0 14px 10px" }}>
                  {asRows(recentBroadcasts).map((row, index) => (
                    <li
                      key={typeof row.text === "string" ? `${row.text}-${index}` : index}
                      data-testid="recent-broadcast-row"
                      style={{ padding: "8px 0", borderBottom: `1px solid ${theme.border}` }}
                    >
                      <span style={{ display: "block", fontSize: 13 }}>
                        {asText(row.text, "Broadcast")}
                      </span>
                      <span
                        style={{
                          display: "block",
                          fontSize: 11,
                          color: theme.textMuted,
                        }}
                      >
                        {[asText(row.to), formatRelativeTime(row.at, now)]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

MessagesPage.propTypes = {
  /**
   * Threads; { id, kind: alert|home|client|family, name, tag, severity,
   * clientId, familyVisible, preview, lastMessageAt, unread, sub, channel,
   * alertLabel, participants: [{ name, role }], context: { title, rows },
   * messages: [{ kind, sender, role, text, at, receipt, self }] }. Typed
   * loosely (array) because the page is intentionally fail-safe for
   * null / malformed rows, and a strict shape would warn for those
   * supported inputs.
   */
  threads: PropTypes.array,
  /** Caregiver directory for broadcast resolution; { id, name, clientIds }. */
  caregivers: PropTypes.array,
  /** Family contact directory; { id, name, clientIds }. */
  familyContacts: PropTypes.array,
  /** Per-client family visibility (consent) map; { [clientId]: boolean }. */
  familyVisibility: PropTypes.object,
  /** Broadcast cohorts; { id, label, kind: caregiver|family, clientIds }. */
  cohorts: PropTypes.array,
  /** Recent broadcast rows; { text, to, at }. */
  recentBroadcasts: PropTypes.array,
  /** The signed-in participant; { role, clientIds } — gates the rail. */
  viewer: PropTypes.object,
  /** Reference time for relative-age display; defaults inside the format helpers. */
  now: PropTypes.number,
  /** Called as onSendMessage({ threadId, text }) after a permitted send. */
  onSendMessage: PropTypes.func,
  /** Called as onSendBroadcast({ cohortId, recipientIds, text }). */
  onSendBroadcast: PropTypes.func,
  /** Called as onEscalate(threadId) from the alert-thread header. */
  onEscalate: PropTypes.func,
  /** Called as onOpenAlert(threadId) from the linked-alert context card. */
  onOpenAlert: PropTypes.func,
};
